import { Graphics, IDestroyOptions, Renderer, Sprite, filters } from "pixi.js"
import { findRendererApp } from "./renderer_app"

/**
 * Graphics shown through a blur that is baked once, after drawing, instead of filtered every frame.
 *
 * A live BlurFilter costs several offscreen passes and render-target switches per frame, and every
 * visible storey carries its own cues. The artwork changes only on a full map render, so soften()
 * renders it through the blur into a texture at that point, outside the frame's filter stack.
 * Baking lazily inside the frame (cacheAsBitmap) is unsafe here: pixi 6 sends a nested filter's
 * output to the enclosing filter's target, and the world surface sits inside the sight mask.
 */
export class SoftenedGraphics extends Graphics {
    readonly blur: InstanceType<typeof filters.BlurFilter>
    private baked?: Sprite

    constructor(strength: number, quality: number) {
        super()
        this.blur = new filters.BlurFilter(strength, quality)
    }

    /** Call after drawing. Without a renderer (headless tests), the blur stays live. */
    soften(): void {
        this.discardBake()
        if (!this.geometry.graphicsData.length) return
        const renderer = findRendererApp()?.renderer as Renderer | undefined
        if (!renderer) {
            this.filters = [this.blur]
            return
        }
        // generateTexture keeps the world transform of a parented object, which would bake the camera in.
        const brush = new Graphics(this.geometry)
        brush.filters = [this.blur]
        const region = brush.getLocalBounds().clone().pad(this.blur.padding)
        region.ceil()
        const texture = renderer.generateTexture(brush, { region, resolution: this.blur.resolution })
        brush.filters = null
        brush.destroy()
        this.baked = new Sprite(texture)
        this.baked.position.set(region.x, region.y)
        this.addChild(this.baked)
    }

    clear(): this {
        this.discardBake()
        return super.clear()
    }

    destroy(options?: IDestroyOptions | boolean): void {
        this.discardBake()
        this.blur.destroy()
        super.destroy(options)
    }

    protected _render(renderer: Renderer): void {
        // Once baked, only the softened child is drawn.
        if (!this.baked) super._render(renderer)
    }

    private discardBake(): void {
        this.filters = null
        if (!this.baked) return
        this.baked.destroy({ texture: true, baseTexture: true })
        this.baked = undefined
    }
}
